import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import {
  Table,
  TableSum,
  TableHeader,
  TableHeaderSum,
  TableHeaderSumForJM,
  TableRow,
  TitleCell,
} from "./FansCount.style";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import {
  getWebUserSchedule,
  getOshicalAndJankatsumasterUserCnt,
} from "../../api/project";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

// API date strings (YYYY-MM-DD / YYYY-MM-DD HH:mm:ss) are interpreted in local time.
const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value).trim();
  const m = text.match(
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (m) {
    const d = new Date(
      +m[1],
      +m[2] - 1,
      +m[3],
      +(m[4] || 0),
      +(m[5] || 0),
      +(m[6] || 0),
    );
    return d.getFullYear() === +m[1] &&
      d.getMonth() === +m[2] - 1 &&
      d.getDate() === +m[3]
      ? d
      : null;
  }
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
};
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dateText = (value) => {
  const d = parseDate(value);
  return d ? dateKey(d).replace(/-/g, "/") : "—";
};
const fmt = (n) => Number(n || 0).toLocaleString("ja-JP");
const DATE_FIELDS = [
  { value: "max_schedule_date", label: "最大スケジュール日" },
  { value: "last_login_at", label: "最終ログイン日" },
  { value: "link_approved_at", label: "連携承認日" },
  { value: "update_at", label: "更新日" },
];
const DATE_SORT_FIELDS = new Set([
  "max_schedule_date",
  "last_login_at",
  "link_approved_at",
  "update_at",
]);
function FansCount() {
  const [infoList, setInfoList] = useState([]);
  const [filterUserName, setFilterUserName] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterUpdateAt, setFilterUpdateAt] = useState("");
  const [filterMaxScheduleDate, setFilterMaxScheduleDate] = useState("");
  const [filterLinkApprovedAt, setFilterLinkApprovedAt] = useState("");
  const [filterLastLoginAt, setFilterLastLoginAt] = useState("");
  //const [searchMode, setSearchMode] = useState<'and' | 'or'>('and');
  const [searchMode, setSearchMode] = useState("and");
  const [sortColumn, setSortColumn] = useState("");
  //const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [sortDirection, setSortDirection] = useState("asc");

  const [oshicalUserCnt, setOshicalUserCnt] = useState("");
  const [jKMUserProCnt, setJKMUserProCnt] = useState("");
  const [jKMUserFansCnt, setJKMUserFansCnt] = useState("");
  const [loading, setLoading] = useState(false);
  const [chartField, setChartField] = useState("max_schedule_date");
  const [showCharts, setShowCharts] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      navigate("/");
      return;
    }
    const getInfos = async () => {
      setLoading(true);
      try {
        const res1 = await getOshicalAndJankatsumasterUserCnt();
        if (res1.code === 200) {
          const sumInfo = res1.data;
          setOshicalUserCnt(sumInfo.oshicalUserCnt);
          setJKMUserProCnt(sumInfo.jKMUserProCnt);
          setJKMUserFansCnt(sumInfo.jKMUserFansCnt);
        }

        const res = await getWebUserSchedule();
        if (res.code === 200 && Array.isArray(res.data)) {
          const fansInfo = res.data.map((item) => ({
            user_name: item.user_name,
            fansCount: item.fan_cnt,
            user_id: item.user_id,
            max_schedule_date: item.max_schedule_date,
            last_login_at: item.last_login_at,
            link_approved_at: item.link_approved_at,
            update_at: item.update_at,
          }));
          setInfoList(fansInfo);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getInfos();
  }, [navigate]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  const matchDateFilter = (value, keyword) => {
    const condition = keyword.trim();
    const text = value == null ? "" : String(value).trim();

    if (!condition) return true;

    // 输入「無」时，只显示空值
    if (condition === "無") {
      return text === "";
    }

    // 普通日期模糊检索
    return text.includes(condition);
  };
  const filteredList = infoList.filter((item) => {
    const conds = [
      (item.user_name?.toString() ?? "").includes(filterUserName),
      (item.user_id?.toString() ?? "").includes(filterUserId),
      matchDateFilter(item.max_schedule_date, filterMaxScheduleDate),
      matchDateFilter(item.last_login_at, filterLastLoginAt),
      matchDateFilter(item.link_approved_at, filterLinkApprovedAt),
      matchDateFilter(item.update_at, filterUpdateAt),
    ];

    return searchMode === "and" ? conds.every(Boolean) : conds.some(Boolean);
  });

  const sortedList = useMemo(() => {
    if (!sortColumn) return filteredList;

    return [...filteredList].sort((a, b) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];

      // NULL、undefined、空字符串统一视为未设置
      const emptyA = valA == null || String(valA).trim() === "";
      const emptyB = valB == null || String(valB).trim() === "";

      const direction = sortDirection === "asc" ? 1 : -1;

      // 空值始终作为最小值
      if (emptyA && emptyB) return 0;
      if (emptyA) return -direction;
      if (emptyB) return direction;

      // 日期字段：按实际日期时间排序
      if (DATE_SORT_FIELDS.has(sortColumn)) {
        const dateA = parseDate(valA);
        const dateB = parseDate(valB);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return (dateA.getTime() - dateB.getTime()) * direction;
      }

      // ファン数：数值排序
      if (sortColumn === "fansCount") {
        return (Number(valA) - Number(valB)) * direction;
      }

      // 用户名、TwitterID：字符串排序
      return (
        String(valA).localeCompare(String(valB), "ja", {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
  }, [filteredList, sortColumn, sortDirection, DATE_SORT_FIELDS]);

  // Chart values reflect the currently filtered list. Each row is one user,
  // and its saved date is NOT an event-history count.
  const chartStats = useMemo(() => {
    const now = new Date();
    const today = startOfDay(now);
    const isSchedule = chartField === "max_schedule_date";
    const valid = filteredList
      .map((row) => ({ row, date: parseDate(row[chartField]) }))
      .filter(({ date }) => date !== null);
    const dated = valid.map(({ row, date }) => ({
      row,
      date,
      dayOffset: Math.round(
        (startOfDay(date).getTime() - today.getTime()) / 86400000,
      ),
    }));
    // Use local calendar-day comparison, avoiding time-of-day differences.
    const dayDiff = (d) => {
      const a = startOfDay(d);
      return Math.round(
        (Date.UTC(a.getFullYear(), a.getMonth(), a.getDate()) -
          Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
          86400000,
      );
    };
    const entries = dated.map((x) => ({ ...x, dayOffset: dayDiff(x.date) }));
    const latest = valid.reduce(
      (best, x) => (!best || x.date > best ? x.date : best),
      null,
    );
    const missing = filteredList.length - valid.length;
    const bins = isSchedule
      ? [
          {
            label: "期限超過",
            count: entries.filter((x) => x.dayOffset < 0).length,
          },
          {
            label: "今日",
            count: entries.filter((x) => x.dayOffset === 0).length,
          },
          {
            label: "1～7日後",
            count: entries.filter((x) => x.dayOffset >= 1 && x.dayOffset <= 7)
              .length,
          },
          {
            label: "8～30日後",
            count: entries.filter((x) => x.dayOffset >= 8 && x.dayOffset <= 30)
              .length,
          },
          {
            label: "31日以上先",
            count: entries.filter((x) => x.dayOffset > 30).length,
          },
          { label: "未設定", count: missing },
        ]
      : [
          {
            label: "今日",
            count: entries.filter((x) => x.dayOffset === 0).length,
          },
          {
            label: "1～7日前",
            count: entries.filter((x) => x.dayOffset <= -1 && x.dayOffset >= -7)
              .length,
          },
          {
            label: "8～30日前",
            count: entries.filter(
              (x) => x.dayOffset <= -8 && x.dayOffset >= -30,
            ).length,
          },
          {
            label: "31～90日前",
            count: entries.filter(
              (x) => x.dayOffset <= -31 && x.dayOffset >= -90,
            ).length,
          },
          {
            label: "91日以上前",
            count: entries.filter((x) => x.dayOffset <= -91).length,
          },
          { label: "未設定", count: missing },
        ];
    const series = Array.from({ length: 14 }, (_, i) => {
      const day = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - (13 - i),
      );
      const key = dateKey(day);
      return {
        date: key,
        count: entries.filter((x) => dateKey(x.date) === key).length,
      };
    });
    const stale = isSchedule
      ? entries
          .filter((x) => x.dayOffset < 0)
          .sort((a, b) => a.dayOffset - b.dayOffset)
      : entries
          .filter((x) => x.dayOffset < 0)
          .sort((a, b) => a.dayOffset - b.dayOffset);
    const oldest10 = stale.slice(0, 10).map((x) => ({
      id: x.row.user_id,
      name: x.row.user_name || x.row.user_id,
      date: dateText(x.date),
      days: Math.abs(x.dayOffset),
    }));
    const within7 = isSchedule
      ? entries.filter((x) => x.dayOffset >= 0 && x.dayOffset <= 7).length
      : entries.filter((x) => x.dayOffset >= -7 && x.dayOffset <= 0).length;
    const over30 = isSchedule
      ? entries.filter((x) => x.dayOffset < 0).length
      : entries.filter((x) => x.dayOffset < -30).length;
    return {
      bins,
      series,
      oldest10,
      latest,
      missing,
      within7,
      over30,
      count: filteredList.length,
      isSchedule,
    };
  }, [filteredList, chartField]);

  const handleClear = () => {
    setFilterUserName("");
    setFilterUserId("");
    setFilterUpdateAt("");
    setFilterMaxScheduleDate("");
    setFilterLinkApprovedAt("");
    setFilterLastLoginAt("");
  };

  const exportToExcel = () => {
    const data = sortedList.map((item) => ({
      TwitterID: item.user_id,
      "プロ／ゲスト名": item.user_name,
      ファン数: item.fansCount,
      最大スケジュール日: item.max_schedule_date,
      最終ログイン日: item.last_login_at,
      連携承認日: item.link_approved_at,
      更新日: item.update_at,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FansCount");
    XLSX.writeFile(workbook, "FansCountList.xlsx");
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  return (
    <MainLayout>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "50px",
            fontSize: "16px",
          }}
        >
          <div
            style={{
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #008000",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              marginRight: "10px",
            }}
          />
          読み込み中...
          <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
        </div>
      ) : (
        <>
          <div
            style={{
              marginBottom: "10px",
              textAlign: "left",
              color: "#008000",
            }}
          >
            <span style={{ fontSize: "18px", fontWeight: "bold" }}>
              ファン-アプリの利用状況
            </span>
          </div>

          <TableSum>
            <thead>
              <tr>
                <TableHeaderSum>推しごとカレンダー利用者数↓</TableHeaderSum>
                <TableHeaderSumForJM>
                  雀活マスター利用プロ数↓
                </TableHeaderSumForJM>
                <TableHeaderSumForJM>
                  雀活マスター利用ファン数↓
                </TableHeaderSumForJM>
              </tr>
            </thead>
            <tbody>
              <TableRow>
                <TitleCell>{oshicalUserCnt}</TitleCell>
                <TitleCell>{jKMUserProCnt}</TitleCell>
                <TitleCell>{jKMUserFansCnt}</TitleCell>
              </TableRow>
            </tbody>
          </TableSum>

          {/* 更新状況ダッシュボード: フィルター結果に連動 */}
          <section className="fc-analytics" aria-label="日付データ分析">
            <style>{`
                    .fc-analytics { background:#fff; border:1px solid #e2e8f0; border-radius:14px; padding:18px; margin:18px 0 22px; color:#0f172a; }
                    .fc-head { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; margin-bottom:14px; }
                    .fc-title { margin:0; font-size:18px; font-weight:700; }
                    .fc-controls { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
                    .fc-controls select, .fc-controls button { padding:7px 10px; border:1px solid #cbd5e1; border-radius:8px; background:white; color:#0f172a; }
                    .fc-summary { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:10px; margin-bottom:18px; }
                    .fc-kpi { border:1px solid #e2e8f0; border-radius:10px; padding:13px; background:#f8fafc; min-width:0; }
                    .fc-kpi-label { font-size:12px; color:#64748b; margin-bottom:6px; }
                    .fc-kpi-val { font-size:22px; font-weight:700; overflow-wrap:anywhere; }
                    .fc-panels { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; }
                    .fc-panel { border:1px solid #e2e8f0; border-radius:10px; padding:12px; min-width:0; }
                    .fc-panel h4 { margin:0 0 4px; font-size:15px; }
                    .fc-note { font-size:12px; color:#64748b; line-height:1.7; margin:8px 0 0; }
                    .fc-aging { margin-top:14px; overflow:auto; }
                    .fc-aging table { width:100%; border-collapse:collapse; font-size:13px; }
                    .fc-aging th, .fc-aging td { padding:7px 9px; border-bottom:1px solid #e2e8f0; text-align:left; }
                    .fc-aging th:last-child, .fc-aging td:last-child { text-align:right; }
                    @media(max-width:850px) { .fc-summary { grid-template-columns:repeat(2,minmax(0,1fr)); } .fc-panels { grid-template-columns:1fr; } }
                    @media(max-width:480px) { .fc-analytics { padding:12px; } .fc-kpi-val { font-size:18px; } .fc-controls { width:100%; } .fc-controls select { flex:1; } }
                `}</style>
            <div className="fc-head">
              <div>
                <h3 className="fc-title">日付データの可視化</h3>
                <div className="fc-note">
                  現在の絞り込み結果：{fmt(chartStats.count)}件
                </div>
              </div>
              <div className="fc-controls">
                <select
                  value={chartField}
                  onChange={(e) => setChartField(e.target.value)}
                  aria-label="分析対象日付"
                >
                  {DATE_FIELDS.map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCharts((prev) => !prev)}
                >
                  {showCharts ? "グラフを閉じる" : "グラフを表示"}
                </button>
              </div>
            </div>
            {showCharts && (
              <>
                <div className="fc-summary">
                  <div className="fc-kpi">
                    <div className="fc-kpi-label">
                      最新の
                      {DATE_FIELDS.find((x) => x.value === chartField)?.label}
                    </div>
                    <div className="fc-kpi-val">
                      {dateText(chartStats.latest)}
                    </div>
                  </div>
                  <div className="fc-kpi">
                    <div className="fc-kpi-label">
                      {chartStats.isSchedule
                        ? "今後7日以内（今日含む）"
                        : "直近7日以内（今日含む）"}
                    </div>
                    <div className="fc-kpi-val">
                      {fmt(chartStats.within7)} 件
                    </div>
                  </div>
                  <div className="fc-kpi">
                    <div className="fc-kpi-label">
                      {chartStats.isSchedule
                        ? "スケジュール日が過去"
                        : "30日超経過"}
                    </div>
                    <div className="fc-kpi-val">
                      {fmt(chartStats.over30)} 件
                    </div>
                  </div>
                  <div className="fc-kpi">
                    <div className="fc-kpi-label">日付未設定</div>
                    <div className="fc-kpi-val">
                      {fmt(chartStats.missing)} 件
                    </div>
                  </div>
                </div>
                <div className="fc-panels">
                  <div className="fc-panel">
                    <h4>
                      {DATE_FIELDS.find((x) => x.value === chartField)?.label}
                      の分布
                    </h4>
                    <p className="fc-note">選択項目の保存日時を期間別に集計</p>
                    <div style={{ height: 260, width: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartStats.bins}
                          margin={{ top: 8, right: 8, bottom: 20, left: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="label"
                            interval={0}
                            angle={-25}
                            textAnchor="end"
                            height={66}
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis allowDecimals={false} width={40} />
                          <Tooltip
                            formatter={(v) => [`${fmt(v)} 件`, "対象件数"]}
                          />
                          <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                            {chartStats.bins.map((x, i) => (
                              <Cell
                                key={x.label}
                                fill={
                                  [
                                    "#2563eb",
                                    "#0d9488",
                                    "#64748b",
                                    "#f59e0b",
                                    "#e87946",
                                    "#94a3b8",
                                  ][i]
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="fc-panel">
                    <h4>直近14日間の最終日付分布</h4>
                    <p className="fc-note">
                      各ユーザーの現在の保存日付が該当する日を集計（更新履歴ではありません）
                    </p>
                    <div style={{ height: 260, width: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={chartStats.series}
                          margin={{ top: 8, right: 10, bottom: 8, left: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            minTickGap={20}
                            tick={{ fontSize: 10 }}
                            tickFormatter={(s) => s.slice(5).replace("-", "/")}
                          />
                          <YAxis allowDecimals={false} width={40} />
                          <Tooltip
                            formatter={(v) => [`${fmt(v)} 件`, "対象件数"]}
                          />
                          <Line
                            dataKey="count"
                            name="件数"
                            type="monotone"
                            stroke="#2563eb"
                            strokeWidth={2.5}
                            dot={{ r: 2 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div className="fc-aging">
                  <h4 style={{ margin: "0 0 8px" }}>
                    {" "}
                    {chartStats.isSchedule
                      ? "最大スケジュール日が過去のユーザー TOP10"
                      : `${DATE_FIELDS.find((x) => x.value === chartField)?.label}が古いユーザー TOP10`}{" "}
                  </h4>
                  <table>
                    <thead>
                      <tr>
                        <th>プロ／ゲスト名</th>
                        <th>TwitterID</th>
                        <th>該当日付</th>
                        <th>経過日数</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartStats.oldest10.length ? (
                        chartStats.oldest10.map((x, i) => (
                          <tr key={`${x.id}-${i}`}>
                            <td>{x.name}</td>
                            <td>{x.id}</td>
                            <td>{x.date}</td>
                            <td>{fmt(x.days)}日</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4}>対象データがありません</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="fc-note">
                  ※
                  グラフ・件数は現在の検索条件に連動します。未設定日は分布の「未設定」に含めています。最大スケジュール日は将来日を基準に区分し、更新日・連携承認日・最終ログイン日は過去日を基準に区分しています。最終ログイン日の正確性はAPI／Batchの元データ定義に依存します。
                </p>
              </>
            )}
          </section>

          {/* フィルターエリア */}
          <div
            style={{
              marginBottom: "10px",
              textAlign: "left",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            <span style={{ fontSize: "13px" }}>絞り込み条件</span>
          </div>

          <div
            style={{
              marginBottom: "10px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <input
              type="text"
              placeholder="プロ／ゲスト名"
              value={filterUserName}
              onChange={(e) => setFilterUserName(e.target.value)}
            />
            <input
              type="text"
              placeholder="TwitterID"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
            />
            <input
              type="text"
              placeholder="最大スケジュール日"
              value={filterMaxScheduleDate}
              onChange={(e) => setFilterMaxScheduleDate(e.target.value)}
            />
            <input
              type="text"
              placeholder="最終ログイン日"
              value={filterLastLoginAt}
              onChange={(e) => setFilterLastLoginAt(e.target.value)}
            />
            <input
              type="text"
              placeholder="連携承認日"
              value={filterLinkApprovedAt}
              onChange={(e) => setFilterLinkApprovedAt(e.target.value)}
            />
            <input
              type="text"
              placeholder="更新日"
              value={filterUpdateAt}
              onChange={(e) => setFilterUpdateAt(e.target.value)}
            />
            <button onClick={handleClear}>クリア</button>
            <button onClick={exportToExcel}>Excel出力</button>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label>
              <input
                type="radio"
                value="and"
                checked={searchMode === "and"}
                onChange={() => setSearchMode("and")}
              />
              AND検索
            </label>
            <label style={{ marginLeft: "10px" }}>
              <input
                type="radio"
                value="or"
                checked={searchMode === "or"}
                onChange={() => setSearchMode("or")}
              />
              OR検索
            </label>
          </div>

          <div
            style={{
              marginBottom: "5px",
              textAlign: "left",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            <span style={{ fontSize: "13px" }}>
              📊 全データ：{infoList.length} 件 ／ フィルター結果：
              {filteredList.length} 件
            </span>
          </div>

          <Table>
            <thead>
              <tr>
                <TableHeader onClick={() => handleSort("user_id")}>
                  TwitterID{renderSortIcon("user_id")}
                </TableHeader>
                <TableHeader onClick={() => handleSort("user_name")}>
                  プロ／ゲスト名{renderSortIcon("user_name")}
                </TableHeader>
                <TableHeader onClick={() => handleSort("fansCount")}>
                  ファン数{renderSortIcon("fansCount")}
                </TableHeader>
                <TableHeader onClick={() => handleSort("max_schedule_date")}>
                  最大スケジュール日{renderSortIcon("max_schedule_date")}
                </TableHeader>
                <TableHeader onClick={() => handleSort("last_login_at")}>
                  最終ログイン日{renderSortIcon("last_login_at")}
                </TableHeader>
                <TableHeader onClick={() => handleSort("link_approved_at")}>
                  連携承認日{renderSortIcon("link_approved_at")}
                </TableHeader>
                <TableHeader onClick={() => handleSort("update_at")}>
                  更新日{renderSortIcon("update_at")}
                </TableHeader>
              </tr>
            </thead>
            <tbody>
              {sortedList.map((item, index) => (
                <TableRow key={index}>
                  <TitleCell>{item.user_id}</TitleCell>
                  <TitleCell>{item.user_name}</TitleCell>
                  <TitleCell>{item.fansCount}</TitleCell>
                  <TitleCell>{item.max_schedule_date}</TitleCell>
                  <TitleCell>{item.last_login_at}</TitleCell>
                  <TitleCell>{item.link_approved_at}</TitleCell>
                  <TitleCell>{item.update_at}</TitleCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </MainLayout>
  );
}

export default FansCount;
